import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  FormField,
  FileUpload,
  Button,
  Alert,
  SpaceBetween,
  RadioGroup,
  Select
} from '@cloudscape-design/components';
import { blissApi } from './blissApi.js';
import SerpKeysModal, { getSavedKeys } from './SerpKeysModal.jsx';

/**
 * Given an ISO date string (e.g. "2026-09-25"), return the number of whole
 * days from today (UTC-safe, rounded up). Returns null if unparseable.
 */
export function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr + 'T00:00:00Z');
  if (isNaN(target.getTime())) return null;
  const now = new Date();
  const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const diffMs = target.getTime() - todayUTC;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

// Headers that suggest a domain/website column (for auto-detection)
const DOMAIN_COLUMN_HINTS = [
  'domain', 'website', 'url', 'site', 'web', 'homepage',
  'company_url', 'company_website', 'company_domain',
  'website_url', 'site_url', 'web_url',
];

/**
 * Parse CSV headers from a file (client-side, no server round-trip).
 * Handles BOM, various encodings.
 */
function parseCsvHeaders(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let text = e.target.result;
        // Strip BOM if present
        if (text.charCodeAt(0) === 0xFEFF) {
          text = text.slice(1);
        }
        // Get first line (headers)
        const firstLine = text.split(/\r?\n/)[0] || '';
        // Simple CSV header parse (handles quoted headers)
        const headers = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < firstLine.length; i++) {
          const ch = firstLine[i];
          if (ch === '"') {
            inQuotes = !inQuotes;
          } else if (ch === ',' && !inQuotes) {
            headers.push(current.trim());
            current = '';
          } else {
            current += ch;
          }
        }
        headers.push(current.trim());
        resolve(headers.filter(h => h.length > 0));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    // Read just the first 8KB — enough for headers
    const slice = file.slice(0, 8192);
    reader.readAsText(slice, 'utf-8');
  });
}

/**
 * Auto-detect which column is likely a domain/website column.
 * Returns the header string or null if no match.
 */
function autoDetectDomainColumn(headers) {
  for (const header of headers) {
    const normalized = header.toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (DOMAIN_COLUMN_HINTS.some(hint => normalized.includes(hint))) {
      return header;
    }
  }
  return null;
}

export default function UploadSection({ onJobStarted }) {
  const [selectedFile, setSelectedFile] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('unknown');
  const [serpUsage, setSerpUsage] = useState(null);
  const [keysModalVisible, setKeysModalVisible] = useState(false);
  const [keyCount, setKeyCount] = useState(0);

  // Domain bypass state
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [domainChoice, setDomainChoice] = useState(null); // null = not answered, 'yes' or 'no'
  const [selectedDomainColumn, setSelectedDomainColumn] = useState(null);
  const [autoDetectedColumn, setAutoDetectedColumn] = useState(null);

  // Load key count on mount
  useEffect(() => {
    setKeyCount(getSavedKeys().length);
  }, [keysModalVisible]);

  // Load SerpAPI usage on mount (sums all saved keys or checks default)
  useEffect(() => {
    const loadUsage = async () => {
      try {
        const keys = getSavedKeys();
        if (keys.length > 0) {
          let totalRemaining = 0;
          let totalPerMonth = 0;
          let totalExtra = 0;
          let plan = 'Free Plan';
          let renewalDate = null;
          for (const key of keys) {
            try {
              const data = await blissApi.checkSerpKey(key);
              totalRemaining += data.total_searches_left || 0;
              totalPerMonth += data.searches_per_month || 0;
              totalExtra += data.extra_credits || 0;
              plan = data.plan_name || plan;
              // Keep the soonest renewal date across keys (most conservative)
              if (data.plan_renewal_date) {
                if (!renewalDate || data.plan_renewal_date < renewalDate) {
                  renewalDate = data.plan_renewal_date;
                }
              }
            } catch {
              // Skip invalid/unreachable keys
            }
          }
          // True capacity = monthly plan quota + any purchased/bonus extra
          // credits. total_searches_left already folds in extra credits, so
          // using searches_per_month alone as the denominator understates
          // capacity (shows "left > total" once extra credits exist).
          setSerpUsage({
            total_searches_left: totalRemaining,
            searches_per_month: totalPerMonth,
            extra_credits: totalExtra,
            capacity: totalPerMonth + totalExtra,
            this_month_usage: (totalPerMonth + totalExtra) - totalRemaining,
            plan_name: plan,
            plan_renewal_date: renewalDate,
          });
        } else {
          const data = await blissApi.getSerpUsage();
          setSerpUsage({
            ...data,
            capacity: (data.searches_per_month || 0) + (data.extra_credits || 0),
          });
        }
      } catch (err) {
        console.error('Failed to load SERP usage:', err);
      }
    };
    loadUsage();
  }, [keysModalVisible]);

  // Harmony username detection
  useEffect(() => {
    const waitForHarmonyUser = async () => {
      const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));
      let retries = 5;
      let login = 'unknown';

      while (retries-- > 0) {
        const user = window?.harmony?.api?.getUserName?.();
        if (user) {
          login = user;
          break;
        }
        await wait(300);
      }
      setUsername(login);
    };
    waitForHarmonyUser();
  }, []);

  // Parse CSV headers when file is selected
  useEffect(() => {
    const parseHeaders = async () => {
      if (selectedFile.length === 0) {
        setCsvHeaders([]);
        setDomainChoice(null);
        setSelectedDomainColumn(null);
        setAutoDetectedColumn(null);
        return;
      }
      try {
        const headers = await parseCsvHeaders(selectedFile[0]);
        setCsvHeaders(headers);

        // Auto-detect domain column
        const detected = autoDetectDomainColumn(headers);
        setAutoDetectedColumn(detected);

        // If we auto-detected a domain column, pre-select it
        if (detected) {
          setSelectedDomainColumn({ label: detected, value: detected });
        }
      } catch {
        setCsvHeaders([]);
      }
    };
    parseHeaders();
  }, [selectedFile]);

  const handleUpload = async () => {
    if (selectedFile.length === 0) {
      setError('Please select a CSV file');
      return;
    }

    const file = selectedFile[0];

    if (!file.name.endsWith('.csv')) {
      setError('Only CSV files are allowed');
      return;
    }

    // Validate domain column selection if user said yes
    if (domainChoice === 'yes' && !selectedDomainColumn) {
      setError('Please select which column contains the domain/website');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const serpKeys = getSavedKeys();
      const domainOptions = domainChoice === 'yes' && selectedDomainColumn
        ? { hasDomain: true, domainColumn: selectedDomainColumn.value }
        : {};
      const result = await blissApi.uploadFile(file, username, serpKeys, domainOptions);
      onJobStarted(result.job_run_id, result.job_identifier);
      setSelectedFile([]);
      setDomainChoice(null);
      setSelectedDomainColumn(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Build column options for the Select dropdown
  const columnOptions = csvHeaders.map(h => ({ label: h, value: h }));

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Upload a CSV file with a 'Company' column to start lead enrichment"
          actions={
            <Button iconName="key" onClick={() => setKeysModalVisible(true)}>
              🔑 API Keys {keyCount > 0 ? `(${keyCount})` : ''}
            </Button>
          }
        >
          ☁️ Upload Lead Data
        </Header>
      }
    >
      <SpaceBetween size="m">
        <Alert type="info">
          <strong>Important:</strong> Your CSV file must contain a column named{' '}
          <code>Company</code> (case-insensitive).
        </Alert>

        <Alert type="warning">
          <strong>Note:</strong> This tool will process up to <strong>200 leads</strong> from your CSV file.
          Each lead uses ~2 SerpAPI searches.
        </Alert>

        {serpUsage && (
          <Alert type={serpUsage.total_searches_left < 20 ? "error" : "success"}>
            🔍 SerpAPI: <strong>{serpUsage.total_searches_left}</strong> searches left out of <strong>{serpUsage.capacity || serpUsage.searches_per_month}</strong> ({serpUsage.plan_name})
            {serpUsage.extra_credits > 0 && ` — incl. ${serpUsage.extra_credits} extra credits`}
            {serpUsage.plan_renewal_date && (() => {
              const d = daysUntil(serpUsage.plan_renewal_date);
              return (
                <>
                  {' '}(expiring on:{' '}
                  <strong style={{ color: '#d91515' }}>{serpUsage.plan_renewal_date}</strong>
                  {d != null && <>, <strong style={{ color: '#d91515' }}>{d} {d === 1 ? 'day' : 'days'} left</strong></>})
                </>
              );
            })()}
            {keyCount > 1 && ` — ${keyCount} keys configured (auto-rotation enabled)`}
          </Alert>
        )}

        {error && <Alert type="error">{error}</Alert>}

        <FormField label="CSV File">
          <FileUpload
            value={selectedFile}
            onChange={({ detail }) => setSelectedFile(detail.value)}
            accept=".csv"
            tokenLimit={1}
            i18nStrings={{
              uploadButtonText: () => 'Choose file',
              dropzoneText: () => 'Drop file to upload',
              removeFileAriaLabel: e => `Remove file ${e + 1}`,
              limitShowFewer: 'Show fewer files',
              limitShowMore: 'Show more files',
              errorIconAriaLabel: 'Error'
            }}
          />
        </FormField>

        {/* Domain bypass section — shown once a file is selected */}
        {csvHeaders.length > 0 && (
          <SpaceBetween size="s">
            <FormField
              label="Does your file already have a website/domain column?"
              description="If yes, we'll use it directly and skip SerpAPI lookup for those leads (saves tokens and time)."
            >
              <RadioGroup
                value={domainChoice}
                onChange={({ detail }) => setDomainChoice(detail.value)}
                items={[
                  { value: 'no', label: 'No — resolve domains via Google search (uses SerpAPI tokens)' },
                  { value: 'yes', label: 'Yes — my file has a column with website/domain info' },
                ]}
              />
            </FormField>

            {domainChoice === 'yes' && (
              <>
                <FormField
                  label="Select the column that contains domain/website"
                  description="Rows with valid domains will skip SerpAPI lookup. Blank or invalid values fall back to SerpAPI."
                >
                  <Select
                    selectedOption={selectedDomainColumn}
                    onChange={({ detail }) => setSelectedDomainColumn(detail.selectedOption)}
                    options={columnOptions}
                    placeholder="Choose column..."
                    filteringType="auto"
                  />
                </FormField>

                {autoDetectedColumn && selectedDomainColumn?.value !== autoDetectedColumn && (
                  <Alert type="info">
                    💡 Auto-detected: <strong>{autoDetectedColumn}</strong> looks like a domain column.{' '}
                    <Button variant="inline-link" onClick={() => setSelectedDomainColumn({ label: autoDetectedColumn, value: autoDetectedColumn })}>Use it</Button>
                  </Alert>
                )}
              </>
            )}
          </SpaceBetween>
        )}

        <Button
          variant="primary"
          onClick={handleUpload}
          loading={uploading}
          disabled={selectedFile.length === 0 || (csvHeaders.length > 0 && domainChoice === null)}
        >
          🚀 Upload & Process
        </Button>
      </SpaceBetween>

      <SerpKeysModal
        visible={keysModalVisible}
        onDismiss={() => setKeysModalVisible(false)}
      />
    </Container>
  );
}
